import React, { useCallback, useMemo } from 'react';
import { Experience, ExperienceStore } from 'util/experience-store';
import { useRelayResource } from 'util/resource-router';
import { getTraceIdFromRelayQueryReference } from 'util/retrieve-relay-traceid';
import { UfoExperienceSuccess } from 'util/ufo-experience';
import { noop } from 'lodash';
import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';
import EditHomepageResource from 'routes/advanced-help-center/resources/edit-homepage';
import ViewHomepageResource from 'routes/advanced-help-center/resources/view-homepage';
import { editHomepageExperience } from 'view/advanced-help-center/edit-homepage/experiences';
import { viewHomepageExperience } from 'view/advanced-help-center/view-homepage/experiences';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import { ExperienceFailure } from '@atlassian/help-center-common-component/analytics/experience-notifier';
import { ScreenName } from '@atlassian/help-center-common-component/constants';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { helpCenterTopicsProductExperience } from '@atlassian/list-of-topics-element';
import type { UFOExperience } from '@atlassian/ufo';
import { useUFOComponentExperience } from '@atlassian/ufo';
import { ElementErrorUI } from '../element-error-ui';
import type { elementErrorBoundaryFragment$key } from './__generated__/elementErrorBoundaryFragment.graphql';
import messages from './messages';

interface ElementErrorBoundaryProps {
    helpLayoutElement: elementErrorBoundaryFragment$key;
    screenName: ScreenName.VIEW_HOMEPAGE | ScreenName.EDIT_HOMEPAGE;
    children: React.ReactNode;
}

const elementExperienceStore = new ExperienceStore();

const experienceMapper = (elementIdentifier: string): Experience => {
    switch (elementIdentifier) {
        case 'EDITOR':
            return Experience.RENDER_EDITOR_ELEMENT;
        case 'HERO':
            return Experience.RENDER_HERO_ELEMENT;
        case 'ANNOUNCEMENT':
            return Experience.RENDER_ANNOUNCEMENT_ELEMENT;
        case 'TOPICS_LIST':
            return Experience.RENDER_TOPICS_LIST_ELEMENT;
        case 'PORTALS_LIST':
            return Experience.RENDER_PORTALS_LIST_ELEMENT;
        case 'SUGGESTED_REQUEST_FORMS_LIST':
            return Experience.RENDER_SUGGESTED_REQUEST_FORMS_LIST_ELEMENT;
        case 'FORGE':
            return Experience.RENDER_FORGE_ELEMENT;
        case 'NO_CONTENT':
            return Experience.RENDER_NO_CONTENT_ELEMENT;
        default:
            return Experience.RENDER_UNKNOWN_ELEMENT;
    }
};

const ElementErrorBoundary: React.FC<ElementErrorBoundaryProps> = ({ screenName, children, helpLayoutElement }) => {
    const data = useFragment(
        graphql`
            fragment elementErrorBoundaryFragment on HelpLayoutElement {
                __typename
                ... on HelpLayoutAnnouncementElement {
                    elementType {
                        key
                    }
                }
                ... on HelpLayoutEditorElement {
                    elementType {
                        key
                    }
                }
                ... on HelpLayoutHeroElement {
                    elementType {
                        key
                    }
                }
                ... on HelpLayoutPortalsListElement {
                    elementType {
                        key
                    }
                }
                ... on HelpLayoutTopicsListElement {
                    elementType {
                        key
                    }
                }
                ... on HelpLayoutSuggestedRequestFormsListElement {
                    elementType {
                        key
                    }
                }
                ... on HelpLayoutNoContentElement {
                    elementType {
                        key
                    }
                }
                ... on HelpLayoutForgeElement {
                    elementType {
                        key
                    }
                }
                ... on HelpLayoutConnectElement {
                    elementType {
                        key
                    }
                }
                ... on QueryError {
                    identifier
                }
            }
        `,
        helpLayoutElement
    );
    const { formatMessage } = useIntl();

    const experience = screenName === ScreenName.VIEW_HOMEPAGE ? viewHomepageExperience : editHomepageExperience;
    let elementIdentifier: string = '';

    if (data.__typename === 'QueryError') {
        elementIdentifier = (data as unknown as { identifier: string | null | undefined }).identifier || 'UNKNOWN';
    } else {
        elementIdentifier =
            (data as unknown as { elementType: { key: string | null | undefined } }).elementType.key || 'UNKNOWN';
    }
    const targetExperience = React.useRef<UFOExperience>(
        elementExperienceStore.createNewUFOExperience({ experience: experienceMapper(elementIdentifier) })
    );

    useUFOComponentExperience(targetExperience.current);

    // Define a list of valid element identifiers
    const validIdentifiers = [
        'EDITOR',
        'HERO',
        'ANNOUNCEMENT',
        'TOPICS_LIST',
        'PORTALS_LIST',
        'SUGGESTED_REQUEST_FORMS_LIST',
        'FORGE',
        'CONNECT',
        'NO_CONTENT',
    ];

    // Default to UNKNOWN if the element type is not recognized
    elementIdentifier = validIdentifiers.includes(elementIdentifier) ? elementIdentifier : 'UNKNOWN';

    // TODO: Remove this check once we have a better way to handle suggested request form errors
    const viewSuggestedRequestFormError = useMemo(() => {
        return screenName === ScreenName.VIEW_HOMEPAGE && elementIdentifier === 'SUGGESTED_REQUEST_FORMS_LIST';
    }, [elementIdentifier, screenName]);

    const rethrowError = useCallback((error: Error) => {
        throw error;
    }, []);

    const { queryReference } = useRelayResource(
        // @ts-ignore
        screenName === ScreenName.EDIT_HOMEPAGE ? EditHomepageResource : ViewHomepageResource
    );

    const markElementExperienceFailed = useCallback(
        (elementExperience: UFOExperience, error: Error | FetchError) => {
            if (!isFetchNetworkOrClientError(error as FetchError)) {
                const traceId = getTraceIdFromRelayQueryReference(queryReference);
                elementExperience
                    .failure({
                        metadata: {
                            traceId,
                            screenName,
                            error: error?.message,
                            statusCode: (error as FetchError)?.status,
                            failureEvent: screenName,
                        },
                    })
                    .then(noop)
                    .catch(noop);
            }
        },
        [queryReference, screenName]
    );

    /**
     * If any error happens inside layout viewer/builder
     * 1. It should fail the entire experience - UFO
     * 2. Return null by default
     * 3. Send error payload to sentry and analytics - through ErrorBoundary
     * 4. Pass reason for the failure -UFO
     */
    const markExperienceFailed = useCallback(
        (error: Error | FetchError) => {
            if (!isFetchNetworkOrClientError(error as FetchError) && !viewSuggestedRequestFormError) {
                const traceId = getTraceIdFromRelayQueryReference(queryReference);
                experience
                    .failure({
                        metadata: {
                            traceId,
                            id: elementIdentifier,
                            error: error?.message,
                            statusCode: (error as FetchError)?.status,
                            failureEvent: screenName,
                        },
                    })
                    .then(noop)
                    .catch(noop);
            }

            if (screenName === ScreenName.VIEW_HOMEPAGE) {
                markElementExperienceFailed(targetExperience.current, error);

                // TODO we need to remove this duplicate experience, we have kept it here since it used in a TOME as of now
                if (elementIdentifier === 'TOPICS_LIST') {
                    markElementExperienceFailed(helpCenterTopicsProductExperience, error);
                }
            }
            if (screenName === ScreenName.EDIT_HOMEPAGE) {
                rethrowError(error as Error);
            }
        },
        [
            viewSuggestedRequestFormError,
            elementIdentifier,
            screenName,
            queryReference,
            experience,
            markElementExperienceFailed,
            rethrowError,
        ]
    );
    const renderError = useCallback(() => {
        return (
            <ElementErrorUI
                errorMessage={formatMessage(messages.errorMessage)}
                elementType={elementIdentifier === 'UNKNOWN' ? data.__typename : elementIdentifier}
            />
        );
    }, [data.__typename, elementIdentifier, formatMessage]);

    const renderErrorWithFailure = (error: Error | FetchError) => {
        if (!isFetchNetworkOrClientError(error as FetchError) && !viewSuggestedRequestFormError) {
            const traceId = getTraceIdFromRelayQueryReference(queryReference);
            return (
                <ExperienceFailure
                    location="element-error-boundary"
                    errorMessage={error.message}
                    traceId={traceId}
                    status={(error as FetchError).status}
                >
                    {renderError()}
                </ExperienceFailure>
            );
        }
        return renderError();
    };
    return (
        <ErrorBoundary
            packageName={screenName}
            id={elementIdentifier}
            renderError={screenName === ScreenName.VIEW_HOMEPAGE ? renderErrorWithFailure : undefined}
            onError={markExperienceFailed}
        >
            {React.Children.only(children)}
            {screenName === ScreenName.VIEW_HOMEPAGE ? (
                <UfoExperienceSuccess experience={targetExperience.current} config={{ metadata: { screenName } }} />
            ) : null}
        </ErrorBoundary>
    );
};

export default ElementErrorBoundary;
